import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type {
    InternalUserFilters,
    InternalAccountStatus,
} from '@/types/internal'
import { Filter } from 'lucide-react'

interface InternalUsersFilterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentFilters: InternalUserFilters
    onApplyFilters: (filters: InternalUserFilters) => void
}

export const InternalUsersFilterDialog: FC<InternalUsersFilterDialogProps> = ({
    open,
    onOpenChange,
    currentFilters,
    onApplyFilters,
}) => {
    const [filters, setFilters] = useState<InternalUserFilters>(currentFilters)

    const handleApply = () => {
        onApplyFilters(filters)
        onOpenChange(false)
    }

    const handleReset = () => {
        const resetFilters: InternalUserFilters = {
            page: 1,
            limit: 20,
        }
        setFilters(resetFilters)
        onApplyFilters(resetFilters)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 p-2">
                            <Filter className="h-4 w-4 text-blue-600" />
                        </div>
                        <DialogTitle>Filter Admin Users</DialogTitle>
                    </div>
                    <DialogDescription>
                        Apply filters to narrow down the admin user list
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Search */}
                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            id="search"
                            placeholder="Search by name, email, or phone..."
                            value={filters.search || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Account Status */}
                    <div className="space-y-2">
                        <Label htmlFor="accountStatus">Account Status</Label>
                        <Select
                            value={(filters.accountStatus as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    accountStatus:
                                        value === 'all'
                                            ? undefined
                                            : (value as InternalAccountStatus),
                                })
                            }
                        >
                            <SelectTrigger id="accountStatus">
                                <SelectValue placeholder="Select account status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="ACTIVE">Active</SelectItem>
                                <SelectItem value="INACTIVE">
                                    Inactive
                                </SelectItem>
                                <SelectItem value="SUSPENDED">
                                    Suspended
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset Filters
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
